import React, { useEffect, useState } from "react";
import FormService from "../../../../components/generic/formbuilder/networkservice/FormService";
import { List, Spin, Typography } from "antd";
import { FormOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useGetRole } from "../../../generic/hooks/useGetRole";
import { RoleToLinkMapper } from "../../../utils/UrlUtils";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import { useRecoilValue } from "recoil";
import { currentConfiguration } from "../../../../service/memory/recoil/configurationstore";
import SearchInput from "../../../generic/elements/search/SearchInput";
import { executeIntent } from "../../../utils/IntentExecutor";
import IntentType from "../../../../intentexecutor/utils/IntentType";
import { getContext } from "../../../../intentexecutor/contextcreator/ContextCreator";
import { useGetOrgIdentifier } from "../../../generic/hooks/useGetOrgIdentifier";

const FormsListComponent = ({ formStatuses }) => {
  const [allFormsData, setAllForms] = useState([]);
  const [searchQuery, setSearchQuery] = useState(undefined);
  const formService = new FormService();
  const [isDataLoading, setDataLoading] = useState(true);
  const navigate = useNavigate();
  const role = useGetRole();
  const userJson = useReadMemory(MemoryType.USER);
  const configuration = useRecoilValue(currentConfiguration);
  const organizationIdentifier = useGetOrgIdentifier();

  const getFormsWithAccessForCurrentUser = async (forms) => {
    const jobTitle = userJson?.jobTitle?.toLowerCase().replaceAll(" ", "-");

    if (!jobTitle) return [];

    try {
      const payload = {
        configurationKey: ["niramaya.forms.access"],
      };
      const result = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload,
        getContext()
      );
      const allowedForms = JSON.parse(result[0]?.configurationValue || "{}")[
        jobTitle
      ];
      if (!Array.isArray(allowedForms)) return [];

      const seen = new Set();

      return forms
        .map(({ category, formTemplates }) => {
          const filteredTemplates = formTemplates.filter(
            ({ metadata, entityId }) => {
              const attributes = JSON.parse(metadata || "{}")?.attributes;
              const hasAccess =
                Array.isArray(attributes) &&
                attributes.some((attr) => allowedForms.includes(attr));
              if (hasAccess && !seen.has(entityId)) {
                seen.add(entityId);
                return true;
              }
              return false;
            }
          );

          return filteredTemplates.length
            ? { category, formTemplates: filteredTemplates }
            : null;
        })
        .filter(Boolean);
    } catch (err) {
      return [];
    }
  };

  async function getForms() {
    try {
      setDataLoading(true);
      if (organizationIdentifier !== undefined) {
        const forms = await formService.getAllFormMetaData(
          organizationIdentifier,
          formStatuses,
          true
        );
        const filteredList = forms.data || [];
        setAllForms(await getFormsWithAccessForCurrentUser(filteredList));
        setDataLoading(false);
      }
    } catch (error) {
      setDataLoading(false);
    }
  }

  const handleClick = (item) => {
    const workforceIdentifier =
      userJson?.jobTitle === "Volunteer"
        ? userJson?.userIdentifier
        : "undefined";
    navigate(
      `${RoleToLinkMapper(role)}form-viewer?templateCode=${
        item?.templateCode
      }&workforceIdentifier=${workforceIdentifier}&withrequest=true`
    );
  };

  useEffect(() => {
    if (searchQuery !== "" && searchQuery !== undefined) {
      setDataLoading(true);
      const lowerSearch = searchQuery.toLowerCase();

      const filtered = allFormsData
        .map(({ category, formTemplates }) => {
          const matchedTemplates = formTemplates.filter((template) =>
            template.title?.toLowerCase().includes(lowerSearch)
          );

          return matchedTemplates.length
            ? { category, formTemplates: matchedTemplates }
            : null;
        })
        .filter(Boolean);

      setAllForms(filtered);
      setDataLoading(false);
    } else {
      getForms();
    }
  }, [configuration, organizationIdentifier, searchQuery]);

  return (
    <div style={{ paddingTop: 20 }}>
      <div className="search-wrapper">
        <SearchInput
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          cssClass="searchContainer"
          searchPlaceholder={"Search forms"}
        />
      </div>

      {isDataLoading ? (
        <div style={{ padding: 16 }}>
          <Spin size="medium" className="spinnerCenter" />
        </div>
      ) : (
        allFormsData?.map((group) => (
          <div key={group.category} style={{ marginBottom: 16 }}>
            <Typography.Title level={4}>{group.category}</Typography.Title>
            <List
              className="forms-list"
              itemLayout="horizontal"
              dataSource={group.formTemplates}
              renderItem={(item) => (
                <List.Item
                  className="formslist-list-item"
                  onClick={() => handleClick(item)}
                >
                  <List.Item.Meta
                    avatar={
                      <FormOutlined
                        style={{ color: "var(--secondary-theme-color)" }}
                      />
                    }
                    title={item?.title}
                    description={item?.description}
                  />
                </List.Item>
              )}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default FormsListComponent;
